import {
    bymoPageGetInnerNavigatablePageById,
    BymoPageProps,
    doppeHideableValueGetVisibleValue,
    DoppeHtmlSitePagesType,
    doppeHtmlSiteResolveAllPostsPageUrl,
    doppeHtmlSiteResolveAllProductsPageUrl,
    doppeHtmlSiteResolveBookOnlinePageUrl,
    doppeHtmlSiteResolveCartPageUrl,
    doppeHtmlSiteResolveEventsPageURL,
    doppeHtmlSiteResolvePricingPlansPageURL
} from '../../../client-server-common';
import {
    autoValueIsAutoValue,
    stringTrimPrefix,
    uiPartCreateMarking,
    validateIsEmail,
    validateIsUrl
} from '@wix/devzai-utils-common';
import type {
    DoppeActionCtaButtonsSettings
} from '../../../client-server-common/types/doppe-action-cta-buttons-settings';
import {
    DoppeActionTypeWidgetInnerCheckoutButtonParts,
    doppeActionTypeWidgetInnerCheckoutButtonPartsCreateName
} from '../../../components/doppe-action-type-widget-inner-checkout-button/doppe-action-type-widget-inner-checkout-button.ui-parts';
import type {
    DoppeActionCTAButton,
    DoppeActionSettingsCTAButtonSettings,
    DoppeLinkSpec
} from '../../../client-server-common/types/doppe-action-cta-button';
import {
    doppeActionCtaButtonLinkResolveTarget,
    doppeActionCtaButtonTypeOpenUrl,
    DoppeActionCTAType,
    doppeActionMultipleCTAButtonIsVisible,
    DoppeLinkSpecEntityType,
    doppeLinkSpecIsEntity,
    doppeLinkSpecIsPage,
    doppeLinkSpecIsUrl
} from '../../../client-server-common/types/doppe-action-cta-button';
import {DoppeViewerLang} from '../../../client-server-common/doppe-viewer/doppe-viewer-lang';
import type {
    DoppeViewerPagesRoutesManager
} from '../../../client-server-common/viewer-pages-routes-manager/doppe-viewer-pages-routes-manager';
import {DoppeDtoInnerPageType} from '../../../client-server-common/types/doppe-dto-inner-page';
import {DoppeViewerPageType} from '../../../client-server-common/viewer-pages/doppe-viewer-pages';
import {
    viewerEntityNavigationResolveBlogPostTarget,
    viewerEntityNavigationResolveEventTarget,
    viewerEntityNavigationResolveProductTarget,
    viewerEntityNavigationResolveServiceTarget,
    viewerListNavigationResolveBlogPostsListTarget,
    viewerListNavigationResolveEventsListTarget,
    viewerListNavigationResolveProductsListTarget,
    viewerListNavigationResolveServicesListTarget
} from '../../../client-server-common/viewer-pages/viewer-entity-navigation';
import type {BymoPageContextProps} from '../../../doppe-sdk/bymo-page/bymo-page-context-props';
import {bymoPageContextPropsResolveEntityInfo} from '../../../doppe-sdk/bymo-page/bymo-page-context-props';

/**
 * Resolves a DoppeLinkSpec to a URL string.
 * Handles URL strings, Page references, and Entity references.
 *
 * When bymoPageContextProps is provided, respects user settings for vertical item/list pages:
 * - useHoppVerticalProductPages, useHoppVerticalServicePages, etc. for entity links
 * - useHoppVerticalProductListPage, useHoppVerticalServiceListPage, etc. for list page links
 *
 * @param linkSpec - The link specification to resolve
 * @param options - Options for URL resolution
 * @returns The resolved URL string, or undefined if cannot be resolved
 */
export function doppeLinkSpecResolveToUrl(
    linkSpec: DoppeLinkSpec,
    options: {
        bymoPageProps: BymoPageProps;
        viewerPagesRoutesManager: DoppeViewerPagesRoutesManager;
        /**
         * The viewer language for proper HTML site URL construction
         */
        viewerLang: DoppeViewerLang;
        /**
         * Whether to show mobile view URLs
         */
        showMobileView?: boolean;
        /**
         * Context props containing user settings for vertical item/list pages.
         * Uses the useHoppVertical*Pages flags to determine whether to
         * use internal routing or external HTML site URLs.
         */
        bymoPageContextProps: BymoPageContextProps;
    }
): string | undefined {
    const {
        bymoPageProps,
        viewerPagesRoutesManager,
        viewerLang,
        showMobileView = false,
        bymoPageContextProps
    } = options;

    // Handle simple URL string
    if (doppeLinkSpecIsUrl(linkSpec)) {
        if (validateIsUrl(linkSpec, {protocols: ['http', 'https'], require_protocol: true})) {
            return linkSpec;
        }
        return linkSpec || undefined;
    }

    // Handle Page link spec - resolve URL from page info
    if (doppeLinkSpecIsPage(linkSpec)) {
        const page = bymoPageGetInnerNavigatablePageById(linkSpec.pageId, bymoPageProps);
        if (page) {
            // Use the navigation functions to respect user settings
            const listNavigationOptions = {
                bymoPageContextProps: bymoPageContextProps,
                viewerLang: viewerLang,
                showMobileView: showMobileView
            };

            switch (page.type) {
                case DoppeDtoInnerPageType.Products: {
                    const result = viewerListNavigationResolveProductsListTarget(listNavigationOptions);
                    if (result.useInternalRouting && viewerPagesRoutesManager) {
                        return viewerPagesRoutesManager.resolvePath({
                            pageType: DoppeViewerPageType.ProductsList
                        });
                    }
                    return result.externalUrl ?? undefined;
                }
                case DoppeDtoInnerPageType.BlogPosts: {
                    const result = viewerListNavigationResolveBlogPostsListTarget(listNavigationOptions);
                    if (result.useInternalRouting && viewerPagesRoutesManager) {
                        return viewerPagesRoutesManager.resolvePath({
                            pageType: DoppeViewerPageType.BlogPostsList
                        });
                    }
                    return result.externalUrl ?? undefined;
                }
                case DoppeDtoInnerPageType.Events: {
                    const result = viewerListNavigationResolveEventsListTarget(listNavigationOptions);
                    if (result.useInternalRouting && viewerPagesRoutesManager) {
                        return viewerPagesRoutesManager.resolvePath({
                            pageType: DoppeViewerPageType.EventsList
                        });
                    }
                    return result.externalUrl ?? undefined;
                }
                case DoppeDtoInnerPageType.Services: {
                    const result = viewerListNavigationResolveServicesListTarget(listNavigationOptions);
                    if (result.useInternalRouting && viewerPagesRoutesManager) {
                        return viewerPagesRoutesManager.resolvePath({
                            pageType: DoppeViewerPageType.ServicesList
                        });
                    }
                    return result.externalUrl ?? undefined;
                }
                case DoppeDtoInnerPageType.Content:
                default:
                    return viewerPagesRoutesManager.contentPage({slug: page.slug});

            }
        }
        return undefined;
    }

    // Handle Entity link spec - resolve URL based on entity type
    if (doppeLinkSpecIsEntity(linkSpec)) {
        const entityType = linkSpec.entityType;

        // Resolve entity info from business entities data
        const resolvedEntityInfo = bymoPageContextPropsResolveEntityInfo(
            bymoPageContextProps,
            entityType,
            linkSpec.entityId
        );

        // If we couldn't resolve a valid slug, we can't generate a proper URL
        if (!resolvedEntityInfo.slug) {
            return undefined;
        }

        const entitySlug = resolvedEntityInfo.slug;
        const entityPageUrl = resolvedEntityInfo.pageUrl;

        // Use the navigation functions to respect user settings
        const entityNavigationOptions = {
            bymoPageContextProps,
            viewerLang: viewerLang,
            showMobileView
        };

        switch (entityType) {
            case DoppeLinkSpecEntityType.Product: {
                const result = viewerEntityNavigationResolveProductTarget(
                    {slug: entitySlug, productPageUrl: entityPageUrl},
                    entityNavigationOptions
                );
                if (result.useInternalRouting && result.slug && viewerPagesRoutesManager) {
                    return viewerPagesRoutesManager.resolvePath({
                        pageType: DoppeViewerPageType.ProductItem,
                        productSlug: result.slug
                    });
                }
                return result.externalUrl ?? undefined;
            }
            case DoppeLinkSpecEntityType.BlogPost: {
                const result = viewerEntityNavigationResolveBlogPostTarget(
                    {slug: entitySlug, pageUrl: entityPageUrl},
                    entityNavigationOptions
                );
                if (result.useInternalRouting && result.slug && viewerPagesRoutesManager) {
                    return viewerPagesRoutesManager.resolvePath({
                        pageType: DoppeViewerPageType.BlogPostItem,
                        postSlug: result.slug
                    });
                }
                return result.externalUrl ?? undefined;
            }
            case DoppeLinkSpecEntityType.Event: {
                const result = viewerEntityNavigationResolveEventTarget(
                    {slug: entitySlug, pageUrl: entityPageUrl},
                    entityNavigationOptions
                );
                if (result.useInternalRouting && result.slug && viewerPagesRoutesManager) {
                    return viewerPagesRoutesManager.resolvePath({
                        pageType: DoppeViewerPageType.EventItem,
                        eventSlug: result.slug
                    });
                }
                return result.externalUrl ?? undefined;
            }
            case DoppeLinkSpecEntityType.Service: {
                const result = viewerEntityNavigationResolveServiceTarget(
                    {slug: entitySlug, servicePageUrl: entityPageUrl},
                    entityNavigationOptions
                );
                if (result.useInternalRouting && result.slug && viewerPagesRoutesManager) {
                    return viewerPagesRoutesManager.resolvePath({
                        pageType: DoppeViewerPageType.ServiceItem,
                        serviceSlug: result.slug
                    });
                }
                return result.externalUrl ?? undefined;
            }
        }
        return undefined;
    }

    return undefined;
}

export function doppeActionCtaButtonTypeGetLinkSettings(
    ctaButton: DoppeActionCTAButton.BaseType,
    options: {
        /**
         * The link specification for the source URL.
         * Can be a URL string, a Page reference, an Entity reference, or null if no source link.
         */
        sourceLinkSpec: DoppeLinkSpec | null,
        viewerLang: DoppeViewerLang;
        isLivePreviewMode: boolean;
        isMobileView: boolean;
        /**
         * The bymoPageProps for resolving page URLs from page IDs
         */
        bymoPageProps: BymoPageProps;
        /**
         * The routes manager for proper URL generation in viewer mode.
         */
        viewerPagesRoutesManager: DoppeViewerPagesRoutesManager;
        /**
         * Context props containing user settings for vertical item/list pages.
         * Uses the useHoppVertical*Pages flags to determine whether to
         * use internal routing or external HTML site URLs.
         */
        bymoPageContextProps: BymoPageContextProps;
    }
) {

    const {
        sourceLinkSpec,
        viewerLang,
        isMobileView,
        isLivePreviewMode,
        bymoPageProps,
        viewerPagesRoutesManager,
        bymoPageContextProps
    } = options;

    const openURL = doppeActionCtaButtonTypeOpenUrl(ctaButton.settings);

    if (openURL) {
        return {
            href: doppeActionCtaButtonTypeResolveURL(ctaButton.settings, {
                sourceLinkSpec: sourceLinkSpec,
                viewerLang: viewerLang,
                showMobileView: isMobileView && isLivePreviewMode,
                bymoPageProps: bymoPageProps,
                viewerPagesRoutesManager: viewerPagesRoutesManager,
                bymoPageContextProps: bymoPageContextProps,
            }),
            target: doppeActionCtaButtonLinkResolveTarget(ctaButton.settings),
            rel: 'noopener',
            tagName: 'a' as const,
        }
    } else {
        return undefined;
    }
}

/**
 * Returns UI part markings for CTA buttons that need them.
 * This is separate from link settings because markings are needed
 * regardless of whether the button uses link navigation or callback navigation.
 */
export function doppeActionCtaButtonTypeGetMarkings(
    ctaButton: DoppeActionCTAButton.BaseType,
    options: {
        actionId: string;
        innerItemId?: string;
    }
) {
    const { type } = ctaButton.settings;
    const { actionId, innerItemId } = options;

    switch (type) {
        case DoppeActionCTAType.Checkout: {
            return {
                ...uiPartCreateMarking(
                    DoppeActionTypeWidgetInnerCheckoutButtonParts.Root,
                    doppeActionTypeWidgetInnerCheckoutButtonPartsCreateName(actionId, innerItemId)
                )
            };
        }
        default:
            return undefined;
    }
}

export function doppeActionCtaButtonsResolveMainURL(
    ctaButtonsSettings: DoppeActionCtaButtonsSettings,
    options: {
        /**
         * The link specification for the source URL.
         * Can be a URL string, a Page reference, an Entity reference, or null if no source link.
         */
        sourceLinkSpec: DoppeLinkSpec | null,
        viewerLang: DoppeViewerLang;
        isMobileView?: boolean;
        isLivePreviewMode?: boolean;
        bymoPageProps: BymoPageProps;
        viewerPagesRoutesManager: DoppeViewerPagesRoutesManager;
        /**
         * Context props containing user settings for vertical item/list pages.
         * Uses the useHoppVertical*Pages flags to determine whether to
         * use internal routing or external HTML site URLs.
         */
        bymoPageContextProps: BymoPageContextProps;
    }
): string | null {

    const {
        sourceLinkSpec,
        viewerLang,
        isMobileView,
        isLivePreviewMode,
        bymoPageProps,
        viewerPagesRoutesManager,
        bymoPageContextProps
    } = options;

    for (let i = 0; i < ctaButtonsSettings.ctaButtons.length; i++) {
        const ctaButton = ctaButtonsSettings.ctaButtons[i];
        if (doppeActionMultipleCTAButtonIsVisible(ctaButton)) {
            const url = doppeActionCtaButtonTypeResolveURL(ctaButton.settings, {
                sourceLinkSpec: sourceLinkSpec,
                viewerLang: viewerLang,
                showMobileView: !!isMobileView && !!isLivePreviewMode,
                bymoPageProps: bymoPageProps,
                viewerPagesRoutesManager: viewerPagesRoutesManager,
                bymoPageContextProps: bymoPageContextProps
            });
            if (url) {
                return url;
            }
        }
    }

    return null;
}


export function doppeActionCtaButtonTypeResolveURL(
    ctaSettings: DoppeActionSettingsCTAButtonSettings,
    options: {
        /**
         * The link specification for the source URL.
         * Can be a URL string, a Page reference, an Entity reference, or null if no source link.
         */
        sourceLinkSpec: DoppeLinkSpec | null,
        viewerLang: DoppeViewerLang;
        showMobileView: boolean;
        bymoPageProps: BymoPageProps;
        viewerPagesRoutesManager: DoppeViewerPagesRoutesManager;
        /**
         * Context props containing user settings for vertical item/list pages.
         * Uses the useHoppVertical*Pages flags to determine whether to
         * use internal routing or external HTML site URLs.
         */
        bymoPageContextProps: BymoPageContextProps;
    }
): string | undefined {

    const {
        sourceLinkSpec,
        viewerLang,
        showMobileView,
        bymoPageProps,
        viewerPagesRoutesManager,
        bymoPageContextProps
    } = options;

    const htmlSiteInfo = bymoPageContextProps.htmlSiteInfo;

    switch (ctaSettings.type) {
        case DoppeActionCTAType.Call: {
            return `tel:${ctaSettings.phone}`;
        }
        case DoppeActionCTAType.Email: {

            if (!validateIsEmail(ctaSettings.email)) {
                return undefined;
            }

            return `mailto:${ctaSettings.email}?subject=${encodeURI(doppeHideableValueGetVisibleValue(ctaSettings.customSubject, ''))}&body=${encodeURI(doppeHideableValueGetVisibleValue(ctaSettings.customMessage, ''))}`
        }
        case DoppeActionCTAType.Share:
        case DoppeActionCTAType.Link: {
            // If url is an AutoValue, use the sourceLinkSpec
            if (autoValueIsAutoValue(ctaSettings.url)) {
                if (sourceLinkSpec === null) {
                    return '';
                }
                // Resolve the sourceLinkSpec using the common function
                return doppeLinkSpecResolveToUrl(sourceLinkSpec, {
                    bymoPageProps: bymoPageProps,
                    viewerPagesRoutesManager: viewerPagesRoutesManager,
                    viewerLang: viewerLang,
                    showMobileView: showMobileView,
                    bymoPageContextProps: bymoPageContextProps
                }) ?? '';
            }

            // Resolve the ctaSettings.url using the common function
            return doppeLinkSpecResolveToUrl(ctaSettings.url, {
                bymoPageProps: bymoPageProps,
                viewerPagesRoutesManager: viewerPagesRoutesManager,
                viewerLang: viewerLang,
                showMobileView: showMobileView,
                bymoPageContextProps: bymoPageContextProps
            }) ?? '';
        }
        case DoppeActionCTAType.IsracardPay: {

            const url = ctaSettings.url;

            if (validateIsUrl(url, {
                protocols: ['http', 'https'],
                require_protocol: true
            })) {
                return url;
            } else {
                return '';
            }
        }
        case DoppeActionCTAType.OpenPage: {
            if (htmlSiteInfo) {
                switch (ctaSettings.pageType) {
                    case DoppeHtmlSitePagesType.Store: {
                        return doppeHtmlSiteResolveAllProductsPageUrl(htmlSiteInfo, viewerLang, showMobileView) ?? undefined;
                    }
                    case DoppeHtmlSitePagesType.BookingServices: {
                        return doppeHtmlSiteResolveBookOnlinePageUrl(htmlSiteInfo, viewerLang, showMobileView) ?? undefined;
                    }
                    case DoppeHtmlSitePagesType.Cart: {
                        return doppeHtmlSiteResolveCartPageUrl(htmlSiteInfo, viewerLang, showMobileView) ?? undefined;
                    }
                    case DoppeHtmlSitePagesType.Blog: {
                        return doppeHtmlSiteResolveAllPostsPageUrl(htmlSiteInfo, viewerLang, showMobileView) ?? undefined;
                    }
                    case DoppeHtmlSitePagesType.PricingPlans: {
                        return doppeHtmlSiteResolvePricingPlansPageURL(htmlSiteInfo, viewerLang, showMobileView) ?? undefined;
                    }
                    case DoppeHtmlSitePagesType.Events: {
                        return doppeHtmlSiteResolveEventsPageURL(htmlSiteInfo, viewerLang, showMobileView) ?? undefined;
                    }
                    default: {
                        return undefined;
                    }
                }
            }

            return undefined
        }
        case DoppeActionCTAType.OpenBlogPost:
        case DoppeActionCTAType.OpenProduct:
        case DoppeActionCTAType.OpenService:
        case DoppeActionCTAType.OpenEvent:
        case DoppeActionCTAType.OpenPricingPlan: {
            // Resolve sourceLinkSpec using the common function
            if (sourceLinkSpec === null) {
                return '';
            }
            return doppeLinkSpecResolveToUrl(sourceLinkSpec, {
                bymoPageProps: bymoPageProps,
                viewerPagesRoutesManager: viewerPagesRoutesManager,
                viewerLang: viewerLang,
                showMobileView: showMobileView,
                bymoPageContextProps: bymoPageContextProps
            }) ?? '';
        }
        case DoppeActionCTAType.Whatsapp: {
            return `https://api.whatsapp.com/send?phone=${stringTrimPrefix(ctaSettings.phone, '+')}&text=${encodeURI(doppeHideableValueGetVisibleValue(ctaSettings.customMessage, ''))}`;
        }
        case DoppeActionCTAType.Navigate: {
            if (!ctaSettings.address) {
                return undefined;
            }

            return `https://www.google.com/maps/dir/?api=1&destination=${encodeURI(ctaSettings.address)}`
        }
        case DoppeActionCTAType.NavigateV2: {
            const { locationCoordinates } = ctaSettings;
            
            if (locationCoordinates) {
                return `https://www.google.com/maps/dir/?api=1&destination=${locationCoordinates.latitude},${locationCoordinates.longitude}`;
            }
            
            return undefined;
        }
        case DoppeActionCTAType.FacebookMessage: {
            return ctaSettings.userName !== undefined ?
                `https://www.m.me/${ctaSettings.userName}?text=${encodeURI(doppeHideableValueGetVisibleValue(ctaSettings.customMessage, ''))}` :
                undefined;
        }
        case DoppeActionCTAType.InstagramMessage: {
            return ctaSettings.userName !== undefined ?
                `https://ig.me/m/${ctaSettings.userName}` :
                undefined;
        }
        case DoppeActionCTAType.Checkout:
        case DoppeActionCTAType.SaveContact:
        case DoppeActionCTAType.WixEcomCheckout: {
            return undefined;
        }
    }
}


export function doppeCtaButtonIsOpenInnerPageOfVertical(ctaButton: DoppeActionCTAButton.BaseType): boolean {


    switch (ctaButton.settings.type) {
        case DoppeActionCTAType.OpenPricingPlan:
        case DoppeActionCTAType.OpenBlogPost:
        case DoppeActionCTAType.OpenProduct:
        case DoppeActionCTAType.OpenService:
        case DoppeActionCTAType.OpenEvent:
        case DoppeActionCTAType.OpenPage: {
            return true;
        }
        default:
            return false;
    }
}
